import { StarRating } from '@/components/common/star-rating';
import type { ReviewItem } from '@/types/shop';
import { formatValidDateString } from '@/utils/date-time';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import dayjs from 'dayjs';
import { useTickSoundInteraction } from '@/hooks';

type Props = {
    tabIndex: number;
    reviewList?: ReviewItem[];
    lastItemRef?: (node?: Element | null) => void;
};
export const ReviewList = ({ tabIndex, reviewList, lastItemRef }: Props) => {
    const { t } = useTranslation();
    const { playTick } = useTickSoundInteraction();
    const isShowReviews = useMemo(() => {
        if (!Array.isArray(reviewList) || !reviewList?.length) {
            return false;
        }
        return true;
    }, [reviewList]);

    if (!isShowReviews) {
        return null;
    }

    return (
        <div className={styles['review-list']}>
            {reviewList?.map((review, index) => {
                const date = dayjs(review?.reviewedDate);

                const year = date.year();
                const month = date.month() + 1;
                const day = date.date();

                return (
                    <div
                        data-modal-region={index === 0}
                        data-modal-region-order={tabIndex}
                        tabIndex={tabIndex}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                playTick();
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                        }}
                        data-interactive
                        aria-label={t('shop.modal.tts.carousel.review_item_image', {
                            poiName: review?.shopName,
                            year,
                            month,
                            day,
                            numberStars: review?.rating,
                            reviewContent: review?.content,
                        })}
                        key={review?.id}
                        className={styles['review-list__item'] + ' box-border border-[calc(2*var(--vh-unit))]! border-transparent'}
                        ref={index === reviewList.length - 1 ? lastItemRef : undefined}
                    >
                        <div className={clsx(styles['review-list__meta'], styles['review-list__meta--vertical'])}>
                            <div>
                                <StarRating rating={review?.rating} />
                            </div>
                            <div className={styles['review-list__meta-date']}>
                                <div className="number">{formatValidDateString(review?.reviewedDate)}</div>
                            </div>
                        </div>
                        <div className={styles['review-list__image']}>
                            <img src={review?.imageSrc} alt={t('common.alt.review_image')} />
                        </div>
                        <div className={styles['review-list__content']}>
                            <div className={clsx(styles['review-list__meta'], styles['review-list__meta--horizontal'])}>
                                <div>
                                    <StarRating rating={review?.rating} />
                                </div>
                                <div className={styles['review-list__meta-date']}>
                                    <div className="number">{formatValidDateString(review?.reviewedDate)}</div>
                                </div>
                            </div>
                            <div className={clsx(styles['review-list__text'], 'scrollbar-custom-gray-99938f scrollbar-w-4')}>{review?.content}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
